<template>
	<video
		class="h-full w-full object-cover"
		ref="videoRef"
		muted
	></video>
</template>

<script setup lang="ts">
import { localNavigation } from '@/mixins/localNavigation'
import type { SbVideoStoryblok } from '@/sbTypes'
import { useAssetStore } from '@/stores/asset'
import { computedAsync, useMediaControls, until } from '@vueuse/core'
import { Swiper } from 'swiper'
import { onMounted, type PropType, type Ref, ref, watch } from 'vue'

const asset = useAssetStore()

let currentVideoIndex = 0
const videoRef = ref()
let currentTime: Ref

// NOTE WHEN WE GET THIS AS AN INPUT WE NEED TO SORT
const props = defineProps({
	blok: Object as PropType<SbVideoStoryblok>,
	parentSwiper: Swiper,
	active: Boolean,
})

const { blok, parentSwiper } = props

const videos = computedAsync(async () => {
	if (!blok?.videos) {
		return []
	}

	const values = await Promise.all(
		blok.videos.map(async (vid) => ({
			url: await asset.getVideoBlobUrl(vid),
		}))
	)

	return values
}, null)

const { changeAllowNext, changeAllowPrev } = localNavigation({
	onNext,
	onPrev,
	parentSwiper,
	slideId: blok?._uid,
	swipingElRef: videoRef,
})

/**
 * Play the video at the currently selected index.
 *
 * If the index is the first or last video (on the current slide), this will also update the
 * `changeAllowNext` and `changeAllowPrev` callbacks to prevent the user from
 * swiping to the next or previous slide as needed.
 */
const playVideo = async () => {
	if (!videos.value) {
		await until(videos).not.toBeNull()
	}

	const resolvedVideos = videos.value as { url: string }[]

	const { playing, currentTime: currentTimeVid } = useMediaControls(videoRef, {
		src: resolvedVideos[currentVideoIndex].url,
	})
	currentTime = currentTimeVid
	playing.value = true
	if (currentVideoIndex === 0) {
		changeAllowNext(false)
		changeAllowPrev(true)
	} else if (currentVideoIndex === resolvedVideos.length - 1) {
		changeAllowNext(true)
		changeAllowPrev(false)
	} else {
		changeAllowNext(false)
		changeAllowPrev(false)
	}
}

function onNext() {
	if (!videos.value || !props.active) return
	if (currentVideoIndex + 1 < videos.value.length) currentVideoIndex++
	playVideo()
}

function onPrev() {
	if (!props.active) return

	// so we can skip back if we try within small period
	const skipBackTime = 1.5
	const time = currentTime.value
	if (time - skipBackTime <= 0 && currentVideoIndex !== 0) currentVideoIndex--
	playVideo()
}

/**
 * When the slide changes, reset the current video index,
 * enable swiping to the next and previous slides, and call `playVideo`.
 */
function onActivate() {
	currentVideoIndex = 0
	changeAllowNext(true)
	changeAllowPrev(true)
	playVideo()
}

watch(
	() => props.active,
	(newVal) => {
		if (newVal) {
			onActivate()
		}
	}
)

onMounted(() => {
	if (props.active) {
		onActivate()
	}
})
</script>
